import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Seo from "../Components/Seo.jsx";
import { trackEvent } from "../utils/analytics.js";
import ReCAPTCHA from "react-google-recaptcha";
import api from "../config/axiosConfig";

import poet from "../assets/Images/poet.jpg";
import { tamilAnthem } from "../constants/data";
import PageHeader from "../Components/PageHeader";

export default function ExpressionInterestForm({}) {
  const navigate = useNavigate();

  // --- Google Address Autocomplete ---
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [addressError, setAddressError] = useState("");

  // Store address components separately (hidden from user)
  const [addressComponents, setAddressComponents] = useState({
    streetNumber: "",
    route: "",
    suburb: "",
    state: "",
    postalCode: "",
    country: "Australia",
  });

  // --- Phone Verification State ---
  const [isVerified, setIsVerified] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(true);
  const [verificationStep, setVerificationStep] = useState("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [verificationErrors, setVerificationErrors] = useState({});
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRefs = useRef([]);
  const resendTimerRef = useRef(null);

  // --- Original Form State ---
  const [childrenCount, setChildrenCount] = useState(1);
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    address: "",
    children: [
      { fullName: "", schoolName: "", schoolGrade: "", communityGrade: "" },
    ],
    otherConcern: "",
    confirmDetails: false,
  });
  const recaptchaRef = useRef(null);
  const successTimeoutRef = useRef(null);

  const gradeOptions = [
    { value: "", label: "Select Grade" },
    { value: "Pre School", label: "Pre School" },
    { value: "Kindergarten", label: "Kindergarten" },
    { value: "Grade 1", label: "Grade 1" },
    { value: "Grade 2", label: "Grade 2" },
    { value: "Grade 3", label: "Grade 3" },
    { value: "Grade 4", label: "Grade 4" },
    { value: "Grade 5", label: "Grade 5" },
    { value: "Grade 6", label: "Grade 6" },
    { value: "Grade 7", label: "Grade 7" },
    { value: "Grade 8", label: "Grade 8" },
    { value: "Grade 9", label: "Grade 9" },
    { value: "Grade 10", label: "Grade 10" },
    { value: "Grade 11", label: "Grade 11" },
    { value: "Grade 12", label: "Grade 12" },
  ];

  useEffect(() => {
    let isMounted = true;

    const loadGoogleMapsScript = () => {
      if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
        initializeAutocomplete();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBrMyjMAzcSM6OrUdab4wKm5CJ3OhqLO2o&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;

      window.initGoogleMaps = () => {
        if (isMounted) initializeAutocomplete();
      };

      script.onerror = () => {
        setAddressError("Failed to load address autocomplete. Please enter your address manually.");
      };

      document.head.appendChild(script);
    };

    const initializeAutocomplete = () => {
      if (!addressInputRef.current) return;
      if (!window.google || !window.google.maps || !window.google.maps.places) return;

      try {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            types: ["address"],
            componentRestrictions: { country: "au" },
            fields: ["address_components", "formatted_address", "geometry"],
          },
        );

        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current.getPlace();

          if (!place || !place.address_components || place.address_components.length === 0) {
            setAddressError("No valid address found. Please try another address or enter manually.");
            return;
          }

          handlePlaceSelect(place);
        });

        setAddressError("");
      } catch (error) {
        console.error("Error initializing autocomplete:", error);
        setAddressError("Address autocomplete error. Please enter address manually.");
      }
    };

    if (isVerified) {
      loadGoogleMapsScript();
    }

    return () => {
      isMounted = false;
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      delete window.initGoogleMaps;
    };
  }, [isVerified]);

  const handlePlaceSelect = (place) => {
    if (!place || !place.address_components) {
      setAddressError("Please select a valid address from the dropdown");
      return;
    }

    const getComponent = (type) => {
      const component = place.address_components.find((c) => c.types.includes(type));
      return component ? component.long_name : "";
    };

    const streetNumber = getComponent("street_number");
    const route = getComponent("route");
    const locality = getComponent("locality");
    const administrativeAreaLevel2 = getComponent("administrative_area_level_2");
    const state = getComponent("administrative_area_level_1");
    const postalCode = getComponent("postal_code");
    const country = getComponent("country");

    let fullDisplayAddress = "";
    if (streetNumber && route) {
      fullDisplayAddress = `${streetNumber} ${route}`;
    } else if (route) {
      fullDisplayAddress = route;
    } else if (streetNumber) {
      fullDisplayAddress = streetNumber;
    }

    let suburb = locality || administrativeAreaLevel2 || "";
    if (suburb) {
      suburb = suburb.replace(/\s*(Victoria|VIC|New South Wales|NSW|Queensland|QLD|Western Australia|WA|South Australia|SA|Tasmania|TAS|Australian Capital Territory|ACT|Northern Territory|NT)\s*/i, "");
      suburb = suburb.replace(/\s*\d{4}\s*/, "");
      suburb = suburb.trim();

      if (suburb) fullDisplayAddress += `, ${suburb}`;
    }

    if (state) fullDisplayAddress += `, ${state}`;
    if (postalCode) fullDisplayAddress += ` ${postalCode}`;
    if (country && country !== "Australia") fullDisplayAddress += `, ${country}`;

    const streetAddressOnly = streetNumber && route ? `${streetNumber} ${route}` : route || streetNumber || "";

    if (!streetAddressOnly) {
      setAddressError("Could not extract street address. Please try selecting a different address or enter manually.");
      return;
    }

    setAddressComponents({
      streetNumber,
      route,
      suburb,
      state,
      postalCode,
      country: country || "Australia",
    });

    setFormData((prev) => ({
      ...prev,
      address: fullDisplayAddress,
    }));

    if (errors.address) {
      setErrors((prev) => ({ ...prev, address: "" }));
    }
    setAddressError("");
  };

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (verificationStep === "otp" && otpInputRefs.current[0]) {
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    }
  }, [verificationStep]);

  useEffect(() => {
    if (resendCooldown > 0) {
      resendTimerRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(resendTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, [resendCooldown]);

  useEffect(() => {
    const isOtpComplete = otp.every((digit) => digit !== "");
    if (
      isOtpComplete &&
      verificationStep === "otp" &&
      !isVerifying &&
      !verificationSuccess
    ) {
      handleVerifyOtp();
    }
  }, [otp]);

  const validateAustralianPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, "");
    const australianMobileRegex = /^(\+?61|0)4\d{8}$/;

    if (!phone) return "Phone number is required";
    if (!australianMobileRegex.test(cleaned) && !australianMobileRegex.test(phone)) {
      return "Must be a valid Australian mobile number (e.g., 04XXXXXXXX or +614XXXXXXXX)";
    }
    return "";
  };

  const formatPhoneForApi = (phone) => {
    let cleaned = phone.replace(/\D/g, "");
    if (cleaned.startsWith("0")) {
      cleaned = "+61" + cleaned.substring(1);
    } else if (cleaned.startsWith("61") && !cleaned.startsWith("+")) {
      cleaned = "+" + cleaned;
    } else if (!cleaned.startsWith("+")) {
      cleaned = "+" + cleaned;
    }
    return cleaned;
  };

  const handleSendOtp = async () => {
    const error = validateAustralianPhoneNumber(phoneNumber);
    if (error) {
      setVerificationErrors({ phone: error });
      return;
    }

    setVerificationErrors({});
    setIsVerifying(true);
    setVerificationSuccess(false);

    try {
      const formattedPhone = formatPhoneForApi(phoneNumber);
      const response = await api.post("/otp/request", {
        ph_no: formattedPhone,
      });

      if (response.data.success) {
        setVerificationStep("otp");
        setOtp(["", "", "", "", "", ""]);
        setResendCooldown(30);
      } else {
        setVerificationErrors({
          phone: response.data.message || "Failed to send OTP. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      let errorMessage = "Failed to send OTP. Please try again.";
      if (error.response?.data?.error) {
        const errors = error.response.data.error;
        errorMessage = Object.values(errors).flat()[0] || errorMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      setVerificationErrors({ phone: errorMessage });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) {
      setVerificationErrors({ otp: "Please enter the 6-digit OTP" });
      return;
    }

    setVerificationErrors({});
    setIsVerifying(true);

    try {
      const formattedPhone = formatPhoneForApi(phoneNumber);
      const response = await api.post("/otp/verify", {
        ph_no: formattedPhone,
        otp_code: enteredOtp,
      });

      if (response.data.success) {
        setVerificationSuccess(true);
        setTimeout(() => {
          setIsVerified(true);
          setShowVerificationModal(false);
          if (!formData.phoneNumber) {
            setFormData((prev) => ({ ...prev, phoneNumber }));
          }
        }, 1500);
      } else {
        setVerificationErrors({
          otp: response.data.message || "Invalid OTP. Please try again.",
        });
        setOtp(["", "", "", "", "", ""]);
        setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      let errorMessage = "Failed to verify OTP. Please try again.";
      if (error.response?.data?.error) {
        const errors = error.response.data.error;
        errorMessage = Object.values(errors).flat()[0] || errorMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      setVerificationErrors({ otp: errorMessage });
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) {
      setVerificationErrors({
        otp: `Please wait ${resendCooldown} seconds before requesting another OTP`,
      });
      return;
    }

    setVerificationErrors({});
    setIsResending(true);
    setVerificationSuccess(false);

    try {
      const formattedPhone = formatPhoneForApi(phoneNumber);
      const response = await api.post("/otp/request", {
        ph_no: formattedPhone,
      });

      if (response.data.success) {
        setOtp(["", "", "", "", "", ""]);
        setResendCooldown(30);
        otpInputRefs.current[0]?.focus();
      } else {
        setVerificationErrors({
          otp: response.data.message || "Failed to resend OTP. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      let errorMessage = "Failed to resend OTP. Please try again.";
      if (error.response?.data?.error) {
        const errors = error.response.data.error;
        errorMessage = Object.values(errors).flat()[0] || errorMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      setVerificationErrors({ otp: errorMessage });
    } finally {
      setIsResending(false);
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const pastedDigits = pastedData.replace(/\D/g, "").slice(0, 6);

    if (pastedDigits.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedDigits.length && i < 6; i++) {
        newOtp[i] = pastedDigits[i];
      }
      setOtp(newOtp);

      const nextEmptyIndex = newOtp.findIndex((digit) => digit === "");
      if (nextEmptyIndex !== -1 && nextEmptyIndex < 6) {
        otpInputRefs.current[nextEmptyIndex]?.focus();
      } else if (newOtp.every((digit) => digit !== "")) {
        otpInputRefs.current[5]?.blur();
      }
    }
  };

  const validateName = (name) => {
    const nameRegex = /^[A-Za-z\s]{3,}$/;
    if (!name) return "This field is required";
    if (!nameRegex.test(name)) return "Must contain only letters and spaces (minimum 3 characters)";
    return "";
  };

  const validatePhone = (phone) => validateAustralianPhoneNumber(phone);

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email) return "Email is required";
    if (!emailRegex.test(email)) return "Must be a valid email address (e.g., name@example.com)";
    return "";
  };

  const validateAddressExists = (address) => {
    if (!address) return "Address is required";
    if (address.length < 3) return "Please enter a valid address";

    if (
      addressComponents.suburb === "" &&
      addressComponents.state === "" &&
      addressComponents.postalCode === ""
    ) {
      return "Please select a valid address from the suggestions";
    }

    return "";
  };

  const validateSchoolField = (value, fieldName = "This field") => {
    if (!value) return `${fieldName} is required`;
    return "";
  };

  const validateSchoolFieldWithLength = (value, fieldName = "This field") => {
    if (!value) return `${fieldName} is required`;
    if (value.length < 3) return `${fieldName} must be at least 3 characters`;
    return "";
  };

  const validateCheckbox = (checked) => {
    if (!checked) return "You must confirm that all details are correct";
    return "";
  };

  const validateChild = (child) => {
    const childErrors = {};
    childErrors.fullName = validateName(child.fullName);
    childErrors.schoolName = validateSchoolFieldWithLength(child.schoolName, "Mainstream name");
    childErrors.schoolGrade = validateSchoolField(child.schoolGrade, "Mainstream grade");
    childErrors.communityGrade = validateSchoolField(child.communityGrade, "WSTSC grade");
    return childErrors;
  };

  const validateForm = () => {
    const newErrors = {};

    newErrors.firstName = validateName(formData.firstName);
    newErrors.lastName = validateName(formData.lastName);
    newErrors.email = validateEmail(formData.email);
    newErrors.phoneNumber = validatePhone(formData.phoneNumber);
    newErrors.address = validateAddressExists(formData.address);
    newErrors.children = formData.children.map((child) => validateChild(child));
    newErrors.confirmDetails = validateCheckbox(formData.confirmDetails);

    if (showRecaptcha && !recaptchaValue) {
      newErrors.recaptcha = "Please complete the reCAPTCHA verification";
    }

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => {
      if (Array.isArray(error)) {
        return error.some((childError) => Object.values(childError).some((err) => err !== ""));
      }
      return error !== "";
    });

    return !hasErrors;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    if (submitStatus.message) setSubmitStatus({ type: "", message: "" });
  };

  const handleChildChange = (index, field, value) => {
    const updatedChildren = [...formData.children];
    updatedChildren[index][field] = value;
    setFormData((prev) => ({ ...prev, children: updatedChildren }));

    if (errors.children && errors.children[index] && errors.children[index][field]) {
      const updatedChildErrors = [...errors.children];
      updatedChildErrors[index][field] = "";
      setErrors((prev) => ({ ...prev, children: updatedChildErrors }));
    }

    if (submitStatus.message) setSubmitStatus({ type: "", message: "" });
  };

  const handleChildrenCountChange = (count) => {
    const newCount = Number(count);
    setChildrenCount(newCount);

    const currentChildren = formData.children;
    if (newCount > currentChildren.length) {
      const newChildren = [...currentChildren];
      for (let i = currentChildren.length; i < newCount; i++) {
        newChildren.push({
          fullName: "",
          schoolName: "",
          schoolGrade: "",
          communityGrade: "",
        });
      }
      setFormData((prev) => ({ ...prev, children: newChildren }));
    } else if (newCount < currentChildren.length) {
      setFormData((prev) => ({
        ...prev,
        children: currentChildren.slice(0, newCount),
      }));
    }
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
    if (value) setErrors((prev) => ({ ...prev, recaptcha: "" }));
  };

  const handleCheckboxFocus = () => {
    if (!showRecaptcha) setShowRecaptcha(true);
  };

  const handleBlur = (field, value, validationFn, ...args) => {
    const error = validationFn(value, ...args);
    setErrors((prev) => ({ ...prev, [field]: error || "" }));
  };

  const handleChildBlur = (index, field, value, validationFn, ...args) => {
    const error = validationFn(value, ...args);
    const updatedChildErrors = [...(errors.children || [])];
    if (!updatedChildErrors[index]) updatedChildErrors[index] = {};
    updatedChildErrors[index][field] = error || "";
    setErrors((prev) => ({ ...prev, children: updatedChildErrors }));
  };

  const prepareSubmissionData = () => {
    const studentFullNames = formData.children.map((child) => child.fullName);
    const mainstreamSchools = formData.children.map((child) => child.schoolName);
    const mainstreamGrades = formData.children.map((child) => child.schoolGrade);
    const communityGrades = formData.children.map((child) => child.communityGrade);

    const streetAddressOnly = addressComponents.streetNumber && addressComponents.route
      ? `${addressComponents.streetNumber} ${addressComponents.route}`
      : addressComponents.route || addressComponents.streetNumber || "";

    return {
      firstname: formData.firstName,
      lastname: formData.lastName,
      email_address: formData.email,
      phone_number: formatPhoneForApi(formData.phoneNumber),
      address_line1: streetAddressOnly,
      address_line2: "",
      suburb: addressComponents.suburb || "",
      state: addressComponents.state || "",
      postal_code: addressComponents.postalCode || "",
      country: addressComponents.country || "Australia",
      number_of_kids: childrenCount,
      student_full_names: studentFullNames,
      mainstreem_school: mainstreamSchools,
      mainstreem_grade: mainstreamGrades,
      comm_school_grade: communityGrades,
      other_note: formData.otherConcern || null,
      data_concern: formData.confirmDetails ? 1 : 0,
      eoi_status: "pending",
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);

    if (!validateForm()) {
      const firstError = document.querySelector(".text-danger");
      if (firstError) firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = prepareSubmissionData();
      const response = await api.post("/eoi", submissionData);

      if (response.data.status === true) {
        // Counted only once the backend accepts it; no form contents are sent.
        trackEvent("generate_lead", { form_name: "eoi", page_path: "/eoi/" });
        setShowSuccessModal(true);
        resetForm();
      } else {
        throw new Error(response.data.message || "Submission failed");
      }
    } catch (error) {
      console.error("Submission error:", error);
      let errorMessage = "Failed to submit the form. Please try again.";

      if (error.response) {
        if (error.response.status === 422) {
          const validationErrors = error.response.data.errors;
          if (validationErrors) {
            const newErrors = {};
            if (validationErrors.firstname) newErrors.firstName = validationErrors.firstname[0];
            if (validationErrors.lastname) newErrors.lastName = validationErrors.lastname[0];
            if (validationErrors.email_address) newErrors.email = validationErrors.email_address[0];
            if (validationErrors.phone_number) newErrors.phoneNumber = validationErrors.phone_number[0];
            if (validationErrors.number_of_kids) newErrors.numberOfKids = validationErrors.number_of_kids[0];
            setErrors((prev) => ({ ...prev, ...newErrors }));
            errorMessage = "Please check the form for errors.";
          }
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else {
          errorMessage = error.response.data?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your internet connection.";
      }

      setSubmitStatus({
        type: "error",
        message: errorMessage,
      });

      setTimeout(() => {
        const statusElement = document.querySelector(".alert-danger");
        if (statusElement) statusElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } finally {
      setIsSubmitting(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaValue(null);
      }
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const handleCloseVerificationModal = () => {
    setShowVerificationModal(false);
    navigate("/");
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      address: "",
      children: [
        { fullName: "", schoolName: "", schoolGrade: "", communityGrade: "" },
      ],
      otherConcern: "",
      confirmDetails: false,
    });
    setAddressComponents({
      streetNumber: "",
      route: "",
      suburb: "",
      state: "",
      postalCode: "",
      country: "Australia",
    });
    setChildrenCount(1);
    setShowRecaptcha(false);
    setErrors({});
    if (addressInputRef.current) addressInputRef.current.value = "";
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
      setRecaptchaValue(null);
    }
  };

  const formatMaskedPhone = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length <= 4) return phone;
    const lastFour = cleaned.slice(-4);
    const firstPart = cleaned.slice(0, -4);
    const masked = firstPart.replace(/./g, "*");
    return `${masked}${lastFour}`;
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value[0];
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (verificationErrors.otp) setVerificationErrors({});

    if (value && index < 5) otpInputRefs.current[index + 1]?.focus();
    if (value && index === 5 && newOtp.every((digit) => digit !== "")) {
      otpInputRefs.current[index]?.blur();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const renderVerificationModal = () => {
    if (!showVerificationModal) return null;

    return (
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 1050 }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold green-text">
                {verificationStep === "phone" ? "Phone Verification" : "Enter OTP"}
              </h5>

              {/* <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleCloseVerificationModal}
                disabled={isVerifying || isResending}
              ></button> */}
            </div>

            <div className="modal-body p-4">
              {verificationStep === "phone" ? (
                <>
                  <p className="text-muted mb-3">
                    Please verify your mobile number to access the Expression of Interest form. You'll receive a 6-digit OTP via SMS.
                  </p>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      className={`form-control ${verificationErrors.phone ? "is-invalid" : ""}`}
                      placeholder="e.g., 0412345678 or +61412345678"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      disabled={isVerifying}
                    />
                    {verificationErrors.phone && (
                      <div className="invalid-feedback">{verificationErrors.phone}</div>
                    )}
                    <small className="text-muted d-block mt-1">
                      Enter a valid mobile number (04XXXXXXXX or +614XXXXXXXX)
                    </small>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-muted mb-3">
                    Enter the 6-digit OTP sent to <strong>{formatMaskedPhone(phoneNumber)}</strong>
                  </p>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">OTP Code</label>
                    <div
                      className="d-flex gap-2 justify-content-center flex-nowrap otp-input-wrapper"
                      onPaste={handleOtpPaste}
                    >
                      {otp.map((digit, idx) => (
                        <input
                          key={idx}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className={`form-control text-center fs-4 fw-bold ${verificationErrors.otp ? "is-invalid" : ""}`}
                          style={{
                            width: "clamp(42px, 13vw, 60px)",
                            height: "clamp(42px, 13vw, 60px)",
                            flexShrink: 1,
                          }}
                          maxLength="1"
                          value={digit}
                          onChange={(e) => handleOtpChange(idx, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                          ref={(el) => (otpInputRefs.current[idx] = el)}
                          disabled={isVerifying || isResending || verificationSuccess}
                        />
                      ))}
                    </div>

                    {verificationSuccess && (
                      <div className="alert alert-success alert-dismissible fade show mt-3" role="alert">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        Phone number verified successfully! Redirecting...
                      </div>
                    )}

                    {verificationErrors.otp && (
                      <div className="text-danger small mt-2">{verificationErrors.otp}</div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer justify-content-between border-0 pt-0 pb-4">
              <button
                type="button"
                className="btn btn-outline-secondary fw-bold rounded-5 px-4 py-2"
                onClick={handleCloseVerificationModal}
                disabled={isVerifying || isResending}
              >
                Close
              </button>

              {verificationStep === "otp" && !verificationSuccess && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-secondary fw-bold rounded-5 px-4 py-2"
                    onClick={handleResendOtp}
                    disabled={isVerifying || isResending || resendCooldown > 0}
                  >
                    {isResending ? "Resending..." : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
                  </button>

                  <button
                    type="button"
                    className="btn green-bg text-white fw-bold rounded-5 px-4 py-2"
                    onClick={handleVerifyOtp}
                    disabled={isVerifying || isResending || verificationSuccess}
                  >
                    {isVerifying ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Verifying...
                      </>
                    ) : (
                      "Verify OTP"
                    )}
                  </button>
                </>
              )}

              {verificationStep === "phone" && (
                <button
                  type="button"
                  className="btn green-bg text-white fw-bold rounded-5 px-4 py-2"
                  onClick={handleSendOtp}
                  disabled={isVerifying}
                >
                  {isVerifying ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Sending...
                    </>
                  ) : (
                    "Send OTP"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isVerified) {
    return (
      <>
        <Seo path="/eoi/" />

        {renderVerificationModal()}

        <div style={{ filter: "blur(4px)", pointerEvents: "none", opacity: 0.5 }}>
          <div className="container-fluid p-0">
            <PageHeader
              title="Enrolment Expression of Interest"
              breadcrumbs={[
                { label: "Home", href: "/", icon: "bi-house", active: false },
                { label: "How to Enrol", href: "/enrolment/" },
                { label: "Expression of Interest", active: true },
              ]}
            />
            <div className="container py-5">
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="card border-0 rounded-4">
                    <div className="card-body p-4 p-md-5">
                      <h2 className="mb-4 fw-bold green-text">Expression of Interest - Application</h2>
                      <p className="text-muted mb-4">Please verify your phone number to continue...</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo path="/eoi/" />

      {showSuccessModal && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.7)" }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-body text-center p-4">
                <i className="bi bi-check-circle-fill text-success fs-1 mb-2 d-block"></i>
                <h4 className="mb-2">Submission Successful!</h4>
                <p className="mb-3 text-start">
                  Thank you for your submission. We sincerely appreciate your interest in the Western Sydney Tamil Study Centre (WSTSC). Our team will review your details and send you an email shortly with the next steps. If you have any questions, please contact us at:
                  <br />
                  <i className="bi bi-envelope me-2" />
                  Email: info@wstsc.org.au
                  <br />
                  <i className="bi bi-phone me-2" />
                  Phone: +61 469 427 599
                </p>
                <button type="button" className="btn green-bg text-white fw-bold rounded-5 px-4 py-2" onClick={closeSuccessModal}>
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container-fluid p-0">
        <PageHeader
          title="Enrolment Expression of Interest"
          breadcrumbs={[
            { label: "Home", href: "/", icon: "bi-house", active: false },
            { label: "How to Enrol", href: "/enrolment/" },
            { label: "Expression of Interest", active: true },
          ]}
        />
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-12">
              <div className="card border-0 rounded-4">
                <div className="card-body p-4 p-md-5">
                  <h2 className="mb-4 fw-bold green-text">
                    <i className="bi bi-vector-pen green-text fs-3 me-2" />
                    Expression of Interest - Application
                  </h2>
                  <p className="text-muted mb-4">
                    Fields marked with <span className="text-danger">*</span> are mandatory
                  </p>

                  <form onSubmit={handleSubmit} noValidate>
                    <h5 className="fw-bold mb-3">Parent Details</h5>

                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label">
                          First Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          style={{ borderColor: errors.firstName ? "#dc3545" : "" }}
                          value={formData.firstName}
                          onChange={(e) => handleInputChange("firstName", e.target.value)}
                          onBlur={(e) => handleBlur("firstName", e.target.value, validateName)}
                          disabled={isSubmitting}
                        />
                        {errors.firstName && <div className="text-danger small mt-1">{errors.firstName}</div>}
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label">
                          Last Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          style={{ borderColor: errors.lastName ? "#dc3545" : "" }}
                          value={formData.lastName}
                          onChange={(e) => handleInputChange("lastName", e.target.value)}
                          onBlur={(e) => handleBlur("lastName", e.target.value, validateName)}
                          disabled={isSubmitting}
                        />
                        {errors.lastName && <div className="text-danger small mt-1">{errors.lastName}</div>}
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="form-label">
                          Email Address <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          style={{ borderColor: errors.email ? "#dc3545" : "" }}
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          onBlur={(e) => handleBlur("email", e.target.value, validateEmail)}
                          disabled={isSubmitting}
                        />
                        {errors.email && <div className="text-danger small mt-1">{errors.email}</div>}
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="form-label">
                          Phone Number <span className="text-danger">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          style={{
                            borderColor: errors.phoneNumber ? "#dc3545" : "",
                            backgroundColor: "#f5f5f5",
                            cursor: "not-allowed",
                          }}
                          value={formData.phoneNumber}
                          onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                          onBlur={(e) => handleBlur("phoneNumber", e.target.value, validatePhone)}
                          disabled={isSubmitting}
                          readOnly={true}
                        />
                        {errors.phoneNumber && <div className="text-danger small mt-1">{errors.phoneNumber}</div>}
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="form-label">
                          Number of Children to Join <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          min="1"
                          max="10"
                          value={childrenCount}
                          onChange={(e) => handleChildrenCountChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <h5 className="fw-bold mb-3">Address Details</h5>
                      {addressError && (
                        <div className="alert alert-warning mb-3">
                          <i className="bi bi-exclamation-triangle-fill me-2"></i>
                          {addressError}
                        </div>
                      )}
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">
                            Street Address <span className="text-danger">*</span>
                          </label>
                          <input
                            ref={addressInputRef}
                            type="text"
                            className="form-control"
                            style={{ borderColor: errors.address ? "#dc3545" : "" }}
                            placeholder="Start typing your street address (e.g., 123 Main Street)"
                            value={formData.address}
                            onChange={(e) => {
                              const typedValue = e.target.value;
                              handleInputChange("address", typedValue);
                              if (addressError) setAddressError("");
                            }}
                            onBlur={(e) => {
                              if (!e.target.value) {
                                handleBlur("address", e.target.value, validateAddressExists);
                              }
                            }}
                            disabled={isSubmitting}
                          />
                          {errors.address && <div className="text-danger small mt-1">{errors.address}</div>}
                          <small className="text-muted d-block mt-1">
                            <i className="bi bi-info-circle me-1"></i>
                            Start typing your street address - suggestions will appear automatically
                          </small>
                        </div>
                      </div>
                    </div>

                    {Array.from({ length: childrenCount }, (_, index) => (
                      <div key={index} className="mt-4">
                        <h5 className="fw-bold mb-3">Child {index + 1} Details</h5>
                        <div className="row g-3">
                          <div className="col-12 col-md-4">
                            <label className="form-label">
                              Child Full Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              style={{ borderColor: errors.children?.[index]?.fullName ? "#dc3545" : "" }}
                              value={formData.children[index]?.fullName || ""}
                              onChange={(e) => handleChildChange(index, "fullName", e.target.value)}
                              onBlur={(e) => handleChildBlur(index, "fullName", e.target.value, validateName)}
                              disabled={isSubmitting}
                            />
                            {errors.children?.[index]?.fullName && (
                              <div className="text-danger small mt-1">{errors.children[index].fullName}</div>
                            )}
                          </div>

                          <div className="col-12 col-md-4">
                            <label className="form-label">
                              Mainstream Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              style={{ borderColor: errors.children?.[index]?.schoolName ? "#dc3545" : "" }}
                              value={formData.children[index]?.schoolName || ""}
                              onChange={(e) => handleChildChange(index, "schoolName", e.target.value)}
                              onBlur={(e) => handleChildBlur(index, "schoolName", e.target.value, validateSchoolFieldWithLength, "Mainstream name")}
                              disabled={isSubmitting}
                            />
                            {errors.children?.[index]?.schoolName && (
                              <div className="text-danger small mt-1">{errors.children[index].schoolName}</div>
                            )}
                          </div>

                          <div className="col-12 col-md-2">
                            <label className="form-label">
                              Mainstream Grade <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              style={{ borderColor: errors.children?.[index]?.schoolGrade ? "#dc3545" : "" }}
                              value={formData.children[index]?.schoolGrade || ""}
                              onChange={(e) => handleChildChange(index, "schoolGrade", e.target.value)}
                              onBlur={(e) => handleChildBlur(index, "schoolGrade", e.target.value, validateSchoolField, "Mainstream grade")}
                              disabled={isSubmitting}
                            >
                              {gradeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {errors.children?.[index]?.schoolGrade && (
                              <div className="text-danger small mt-1">{errors.children[index].schoolGrade}</div>
                            )}
                          </div>

                          <div className="col-12 col-md-2">
                            <label className="form-label">
                              WSTSC Grade <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              style={{ borderColor: errors.children?.[index]?.communityGrade ? "#dc3545" : "" }}
                              value={formData.children[index]?.communityGrade || ""}
                              onChange={(e) => handleChildChange(index, "communityGrade", e.target.value)}
                              onBlur={(e) => handleChildBlur(index, "communityGrade", e.target.value, validateSchoolField, "WSTSC grade")}
                              disabled={isSubmitting}
                            >
                              {gradeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {errors.children?.[index]?.communityGrade && (
                              <div className="text-danger small mt-1">{errors.children[index].communityGrade}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="mt-4">
                      <label className="form-label fw-bold h5">Other Concern or Note</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={formData.otherConcern}
                        onChange={(e) => handleInputChange("otherConcern", e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="form-check my-4">
                      <div>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="confirmDetails"
                          checked={formData.confirmDetails}
                          onChange={(e) => {
                            handleInputChange("confirmDetails", e.target.checked);
                            handleCheckboxFocus();
                          }}
                          style={{ borderColor: errors.confirmDetails ? "#dc3545" : "" }}
                          disabled={isSubmitting}
                        />
                        <label className="form-check-label ms-2" htmlFor="confirmDetails">
                          I confirm that the information provided in this Expression of Interest application is true and correct. I understand that WSTSC will contact me by email with further enrolment process details. <span className="text-danger">*</span>
                        </label>
                      </div>
                      {errors.confirmDetails && <div className="text-danger small mt-1">{errors.confirmDetails}</div>}
                    </div>

                    {showRecaptcha && (
                      <div className="mb-4">
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey="6LeEyvgrAAAAALU9ZNkyPpobFU1EdLrj-NAWNzZD"
                          onChange={handleRecaptchaChange}
                        />
                        {errors.recaptcha && <div className="text-danger small mt-1">{errors.recaptcha}</div>}
                      </div>
                    )}

                    <button
                      type="submit"
                      aria-label="Enrol Now"
                      className="btn green-bg fw-bold text-white rounded-5 px-4 py-2 fs-6 border-0 text-uppercase"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Submitting...
                        </>
                      ) : (
                        "Enrol Now"
                      )}
                    </button>

                    {submitStatus.message && submitStatus.type === "error" && (
                      <div className="alert alert-danger alert-dismissible mt-3 fade show mb-4" role="alert">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {submitStatus.message}
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setSubmitStatus({ type: "", message: "" })}
                        ></button>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
